import INVENTORY_API_BASE_URL from "../../config/api";
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "./expense.css";

const initialCategoryOptions = {
  "Current Assets": ["Cash", "Accounts Receivable", "Inventory"],
  "Fixed Assets": ["Machinery", "Office Equipment", "Land"],
  "Fixed Costs": ["Rent", "Salaries", "Insurance"],
  "Variable Costs": ["Raw Materials", "Commissions", "Packaging"],
  "Operating Expenses": ["Utilities", "Advertising", "Maintenance"],
  "Non-Operating Expenses": ["Interest", "Taxes", "Depreciation"],
};

const expenseTypes = ["Direct Expenses", "Indirect Expenses"];

const ExpenseForm = () => {
  const navigate = useNavigate();
  const [vendorList, setVendorList] = useState([]);
  const [expenseData, setExpenseData] = useState({
    category: "",
    subcategory: "",
    vendor: "",
    date: "",
    expenseType: "",
    description: "",
    paymentMethod: "",
    invoiceNumber: "",
    amount: "",
  });

  const [uploadedFile, setUploadedFile] = useState(null);
  const [categoryOptions, setCategoryOptions] = useState(
    initialCategoryOptions
  );

  useEffect(() => {
    const fetchVendors = async () => {
      try {
        const response = await axios.get(
          `${INVENTORY_API_BASE_URL}/get-vendors`
        );
        setVendorList(response.data);
      } catch (error) {
        console.error("Failed to fetch vendors:", error);
      }
    };
    fetchVendors();
  }, []);

  const capitalizeWords = (str) =>
    str.replace(/\b\w/g, (char) => char.toUpperCase());

  const handleChange = (e) => {
    const { name, value } = e.target;
    let updatedValue = value;

    if (["vendor", "description", "paymentMethod"].includes(name)) {
      updatedValue = capitalizeWords(value);
    }

    if (name === "category") {
      setExpenseData({ ...expenseData, category: value, subcategory: "" });
    } else {
      setExpenseData({ ...expenseData, [name]: updatedValue });
    }
  };

  const handleFileChange = (e) => {
    setUploadedFile(e.target.files[0]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const formData = new FormData();
      for (const key in expenseData) {
        formData.append(key, expenseData[key]);
      }

      if (uploadedFile) {
        formData.append("document", uploadedFile);
      }

      const response = await axios.post(
        `${INVENTORY_API_BASE_URL}/expenses`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.status === 200) {
        alert("Expense added successfully!");
        setExpenseData({
          category: "",
          subcategory: "",
          vendor: "",
          date: "",
          expenseType: "",
          description: "",
          paymentMethod: "",
          invoiceNumber: "",
          amount: "",
        });
        setUploadedFile(null);
        navigate("/Expensedata");
      }
    } catch (error) {
      console.error("Error adding expense:", error);
      alert("Failed to add expense.");
    }
  };

  const handleAddCategory = () => {
    const newCategory = prompt("Enter new category:");
    if (newCategory) {
      const capNewCategory = capitalizeWords(newCategory.trim());
      if (!categoryOptions[capNewCategory]) {
        setCategoryOptions({ ...categoryOptions, [capNewCategory]: [] });
      } else {
        alert("Category already exists.");
      }
    }
  };

  const handleAddSubcategory = () => {
    if (!expenseData.category) {
      alert("Please select a category first.");
      return;
    }
    const newSubcategory = prompt("Enter new subcategory:");
    if (newSubcategory) {
      const capSub = capitalizeWords(newSubcategory.trim());
      if (!categoryOptions[expenseData.category].includes(capSub)) {
        setCategoryOptions({
          ...categoryOptions,
          [expenseData.category]: [
            ...categoryOptions[expenseData.category],
            capSub,
          ],
        });
      } else {
        alert("Subcategory already exists.");
      }
    }
  };

  return (
    <div className="expenseform-container">
      <h3 style={{ textAlign: "center" }}>Create Expense</h3>
      <form onSubmit={handleSubmit}>
        <div>
          <label>Category :</label>
          <div style={{ display: "flex", gap: "8px" }}>
            <select
              name="category"
              value={expenseData.category}
              onChange={handleChange}
              required
            >
              <option value="">-- Select Category --</option>
              {Object.keys(categoryOptions).map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
            <button type="button" onClick={handleAddCategory}>
              Add
            </button>
          </div>
        </div>

        <div>
          <label>Sub-Category :</label>
          <div style={{ display: "flex", gap: "8px" }}>
            <select
              name="subcategory"
              value={expenseData.subcategory}
              onChange={handleChange}
              required
              disabled={!expenseData.category}
            >
              <option value="">-- Select Subcategory --</option>
              {expenseData.category &&
                categoryOptions[expenseData.category]?.map((sub) => (
                  <option key={sub} value={sub}>
                    {sub}
                  </option>
                ))}
            </select>
            <button type="button" onClick={handleAddSubcategory}>
              Add
            </button>
          </div>
        </div>

        <div>
          <label>Vendor :</label>
          <select
            name="vendor"
            value={expenseData.vendor}
            onChange={handleChange}
            required
          >
            <option value="">-- Select Vendor --</option>
            {vendorList.map((vendor) => (
              <option key={vendor._id} value={vendor.name}>
                {vendor.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label>Date :</label>
          <input
            type="date"
            name="date"
            value={expenseData.date}
            onChange={handleChange}
            required
          />
        </div>

        <div>
          <label>Expense Type :</label>
          <select
            name="expenseType"
            value={expenseData.expenseType}
            onChange={handleChange}
            required
          >
            <option value="">-- Select Expense Type --</option>
            {expenseTypes.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label>Description :</label>
          <input
            type="text"
            name="description"
            value={expenseData.description}
            onChange={handleChange}
            required
          />
        </div>

        <div>
          <label>Payment Method :</label>
          <input
            type="text"
            name="paymentMethod"
            value={expenseData.paymentMethod}
            onChange={handleChange}
            required
          />
        </div>

        <div>
          <label>Invoice Number :</label>
          <input
            type="text"
            name="invoiceNumber"
            value={expenseData.invoiceNumber}
            onChange={handleChange}
            required
          />
        </div>

        <div>
          <label>Amount :</label>
          <input
            type="text"
            name="amount"
            value={expenseData.amount}
            onChange={handleChange}
            required
          />
        </div>

        <div>
          <label>Upload Receipt/Invoice :</label>
          <input
            type="file"
            name="document"
            accept=".jpg,.jpeg,.png,.pdf"
            onChange={handleFileChange}
            required
          />
        </div>

        <button type="submit">Add Expense</button>
      </form>
    </div>
  );
};

export default ExpenseForm;
